import { Button } from '@/shared/ui/Button';
import { Typography } from '@/shared/ui/Typography/Typography';

import LinkArrowIcon from '@/assets/icons/linkArrow.svg?react';
import TelegramIcon from '@/assets/icons/telegram.svg?react';
import WhatsappIcon from '@/assets/icons/whatsapp.svg?react';
import { Input } from '@/shared/ui/Input/Input';
import { useAppSelector } from '@/app/store/hooks';
import { selectContacts } from '@/entities/home_content/model/selectors';
import { useState } from 'react';
import { FeedbackSuccessCard } from './FeedbackSuccessCard';
import { cn } from '@/shared/lib/utils';
import { IconButton } from '@/shared/ui/Button/IconButton';

import CloseIcon from '@/assets/icons/close.svg?react';
import { Link } from 'react-router-dom';
import { FeedbackContentType } from '@/shared/ui/Modal/ui/ModalFeedback';
import { handleSendReq } from '@/shared/lib/utils/contact';

type userInfoType = {
  name: {
    value: string;
    error: string;
  };
  phone: {
    value: string;
    error: string;
  };
  term: boolean;
};

type FeedbackPropsType = {
  content?: FeedbackContentType;
  className?: string;
  isModal?: boolean;
  link?: string;
  handleDataSend?: () => void;
  handlModalClose?: () => void;
};

export function Feedback({
  className,
  content,
  handleDataSend,
  handlModalClose,
  isModal,
  link,
}: FeedbackPropsType) {
  const contacts = useAppSelector(selectContacts);

  const [userInfo, setUserInfo] = useState<userInfoType>({
    name: { value: '', error: '' },
    phone: { value: '', error: '' },
    term: false,
  });

  const [success, setSuccess] = useState<boolean>(false);

  const validate = () => {
    let isValid = true;
    const newUserInfo = { ...userInfo };

    if (!userInfo.name.value.trim()) {
      newUserInfo.name.error = 'Введите имя';
      isValid = false;
    } else {
      newUserInfo.name.error = '';
    }

    const phoneRegex = /^\+?[0-9\s-]{10,15}$/;
    if (!userInfo.phone.value.trim()) {
      newUserInfo.phone.error = 'Введите номер телефона';
      isValid = false;
    } else if (!phoneRegex.test(userInfo.phone.value)) {
      newUserInfo.phone.error = 'Неверный формат номера';
      isValid = false;
    } else {
      newUserInfo.phone.error = '';
    }

    setUserInfo(newUserInfo);
    return isValid;
  };

  const handleInputChange = (field: 'name' | 'phone', value: string) => {
    setUserInfo((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value: value?.trim() || '',
        error: '',
      },
    }));
  };

  const handleCheckboxChange = (value: boolean) => {
    setUserInfo((prev) => ({ ...prev, term: value }));
  };

  const hadleCloseSuccess = () => {
    setUserInfo({
      name: { value: '', error: '' },
      phone: { value: '', error: '' },
      term: false,
    });
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!userInfo.term) {
      alert('Пожалуйста, подтвердите согласие на обработку данных.');
      return;
    }

    if (!validate()) {
      return;
    }
    const result = await handleSendReq(
      userInfo.name.value,
      userInfo.phone.value,
      userInfo.term,
      window.location.href
    );

    if (result && result.firstname) {
      if (handleDataSend) handleDataSend();

      setSuccess(true);
    }
  };

  const title = content ? content.title : 'Оставьте заявку на консультацию';
  const description = content
    ? (content.description ?? null)
    : 'Наш эксперт свяжется с вами в ближайшее время, чтобы подобрать подходящую недвижимость или ответить на интересующие вопросы.';

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[20px] bg-[url('/images/feedback_bg.png')] bg-cover bg-center bg-no-repeat",
        isModal
          ? 'p-[20px] xl:p-[80px]'
          : 'p-[10px] md:p-[20px] xl:px-[80px] xl:py-[56px]',
        className
      )}
      id="feedback"
    >
      <div
        className="relative m-auto flex flex-col gap-[20px] overflow-hidden rounded-[20px] bg-[#ffffffb2] p-[20px] backdrop-blur-[25px]
                    md:flex-row
                "
      >
        <div className="flex h-full w-auto flex-col gap-[16px] md:w-1/2 xl:gap-[20px]">
          <Typography
            tag="h2"
            className="text-[24px] font-semibold leading-[0.98em] xl:text-[26px]"
          >
            {title}
          </Typography>
          {description && (
            <Typography className="text-[14px] font-[500] md:text-[14px]">
              {description}
            </Typography>
          )}

          <form
            className="flex flex-col gap-[16px] xl:gap-[20px]"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col gap-[8px] md:gap-[12px]">
              <Input
                className="h-[43px] rounded-[15px] border-none"
                placeholder="Имя"
                type="text"
                value={userInfo.name.value}
                error={userInfo.name.error}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />

              <Input
                className="h-[43px] rounded-[15px] border-none"
                name="phone"
                type="tel"
                value={userInfo.phone.value}
                error={userInfo.phone.error}
                placeholder="Телефон"
                pattern="^\\+?[0-9\\s-]{10,15}$"
                onChange={(e) => handleInputChange('phone', e.target.value)}
                required
              />

              <div className="flex flex-row items-start gap-2">
                <input
                  id="agreeTerms"
                  name="agreeTerms"
                  type="checkbox"
                  checked={userInfo.term}
                  className="mt-1  text-[var(--black-grey)] accent-[var(--black-grey)] focus:ring-0"
                  onChange={(e) => handleCheckboxChange(e.target.checked)}
                  required
                />
                <label htmlFor={'agreeTerms'}>
                  <Typography className="text-[8px] text-[var(--black-grey)]">
                    Я полностью ознакомился и согласен с
                    <Link to="/privacy-policy" className="text-[#116ACC]">
                      {' '}
                      Пользовательским соглашением, Политикой обработки
                      персональных данных и файлов Cookie.
                    </Link>
                    , и предоставляю конкретное, предметное, информированное,
                    сознательное и однозначное Согласие на обработку моих
                    персональных данных.
                  </Typography>
                </label>
              </div>
            </div>

            <div className="flex flex-col gap-[8px] md:gap-[12px]">
              <Button
                endIcon={<LinkArrowIcon className="h-[30px] w-[30px]" />}
                text="Заказать звонок"
                bg="black"
                className="h-[38px] w-full p-[4px] pl-[16px] text-[16px] md:h-[42px] md:pr-[6px]"
                type="submit"
              />

              <div className="flex flex-row gap-[8px] md:gap-[12px]">
                {contacts?.telegramUrl && (
                  <a
                    href={contacts.telegramUrl}
                    className="w-1/2 flex-1"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="flex h-[39px] items-center justify-between gap-[12px] rounded-full bg-white p-[4px] pl-[15px]">
                      <Typography className="h-min w-fit text-[16px]">
                        Связаться
                      </Typography>
                      <TelegramIcon className="h-[35px] w-[35px]" />
                    </div>
                  </a>
                )}

                {contacts?.whatsappUrl && (
                  <a
                    href={contacts.whatsappUrl}
                    className="w-1/2 flex-1"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="flex h-[39px] items-center justify-between gap-[12px] rounded-full bg-white p-[2px] pl-[15px]">
                      <Typography className="h-min w-fit text-[16px]">
                        Связаться
                      </Typography>
                      <WhatsappIcon className="h-[37px] w-[37px]" />
                    </div>
                  </a>
                )}
              </div>
            </div>
          </form>
        </div>

        <div className="h-[200px] w-full rounded-[10px] bg-[url('/images/feedback_bg.png')] bg-cover bg-center bg-no-repeat p-[10px] md:h-auto md:w-1/2">
          <a
            href={`tel:${contacts ? contacts.phoneNumbers[0] : '+7 (495)137-97-65'}`}
          >
            <Button
              text={contacts ? contacts.phoneNumbers[0] : '+7 (495)137-97-65'}
              className="float-end flex h-[34px] rounded-[15px] px-[12px] text-[14px] font-[400] md:h-[43px] md:text-[16px]"
              bg="white"
            />
          </a>
        </div>
      </div>

      {isModal && (
        <IconButton
          icon={<CloseIcon className="h-8px w-[8px]" />}
          className={cn(
            'absolute right-[10px] top-[10px] h-[16px] w-[16px] bg-white/50 xl:right-[20px] xl:top-[20px]'
          )}
          onClick={handlModalClose}
        />
      )}

      {success && <FeedbackSuccessCard handleClose={hadleCloseSuccess} />}
    </div>
  );
}
